import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import ApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import moment from 'moment';

import type {
  HarborOperatorDashboardStatusItem,
  HarborOperatorDashboardTopHarbor,
  HarborOperatorDashboardTrendPoint,
} from '@/types/harbor-operator-dashboard.types';
import { formatFinnishCurrency, formatFinnishNumber } from '@/utils';
import { normalizeTrendPoints } from '@/utils/harborOperatorDashboardTrend';

import { getBookingStatusMessageId } from './harborDashboardStatus';

export { normalizeTrendPoints } from '@/utils/harborOperatorDashboardTrend';

const formatPeriodLabel = (period: string) => {
  const parsed = moment(period, ['YYYY-MM', moment.ISO_8601], true);
  return parsed.isValid() ? parsed.format('MMM YY') : period;
};

const chartToolbar = { show: false };

/** ApexCharts bar/area paths read `globals.stroke.colors`; never pass `stroke: undefined`. */
const chartStrokeColors = ['var(--tw-primary)'];
const chartDonutColors = [
  'var(--tw-primary)',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#6366f1',
  '#94a3b8',
];

interface TrendChartProps {
  title: string;
  subtitle: string;
  data: HarborOperatorDashboardTrendPoint[];
  mode: 'bookings' | 'revenue';
  loading?: boolean;
}

export const HarborDashboardTrendChart = ({
  title,
  subtitle,
  data,
  mode,
  loading,
}: TrendChartProps) => {
  const intl = useIntl();
  const trendPoints = useMemo(() => normalizeTrendPoints(data), [data]);

  const categories = useMemo(
    () => trendPoints.map((d) => formatPeriodLabel(d.period)),
    [trendPoints]
  );

  const series = useMemo(
    () =>
      mode === 'bookings'
        ? [
            {
              name: intl.formatMessage({ id: 'DASHBOARD.CHART.SERIES_BOOKINGS' }),
              data: trendPoints.map((d) => d.bookings),
            },
          ]
        : [
            {
              name: intl.formatMessage({
                id: 'DASHBOARD.CHART.SERIES_NET_REVENUE',
              }),
              data: trendPoints.map((d) => d.net_revenue),
            },
          ],
    [intl, mode, trendPoints]
  );

  const chartKey = useMemo(
    () => `${mode}-${categories.join('|')}-${series[0].data.join('|')}`,
    [mode, categories, series]
  );

  const options: ApexOptions = useMemo(() => {
    const revenueMax = Math.max(...series[0].data, 0);

    return {
      chart: {
        type: 'area',
        height: 280,
        toolbar: chartToolbar,
        fontFamily: 'inherit',
        animations: { enabled: true },
      },
      colors: chartStrokeColors,
      dataLabels: { enabled: false },
      xaxis: {
        categories,
        labels: { style: { colors: 'var(--tw-gray-500)', fontSize: '11px' } },
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        tickAmount: mode === 'revenue' && revenueMax === 0 ? 4 : undefined,
        labels: {
          style: { colors: 'var(--tw-gray-500)', fontSize: '11px' },
          formatter: (val) => {
            const n = Number(val);
            if (!Number.isFinite(n)) {
              return '0';
            }
            if (mode === 'revenue') {
              return formatFinnishNumber(n, { maximumFractionDigits: 0 });
            }
            return String(Math.round(n));
          },
        },
      },
      grid: { borderColor: 'var(--tw-gray-200)', strokeDashArray: 4 },
      tooltip: {
        y: {
          formatter: (val) =>
            mode === 'revenue'
              ? formatFinnishCurrency(val)
              : intl.formatMessage(
                  { id: 'DASHBOARD.CHART.BOOKINGS_COUNT' },
                  { count: Math.round(Number(val)) }
                ),
        },
      },
      stroke: {
        curve: 'smooth',
        width: 2,
        colors: chartStrokeColors,
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 0.35,
          opacityFrom: mode === 'bookings' ? 0.45 : 0.35,
          opacityTo: 0.05,
        },
      },
    };
  }, [categories, intl, mode, series]);

  return (
    <div className="card rounded-lg border border-gray-500 shadow-md p-4 h-full">
      <div className="mb-3">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <p className="text-xs text-gray-500">{subtitle}</p>
      </div>
      {loading ? (
        <p className="text-sm text-gray-500 py-16 text-center">
          {intl.formatMessage({ id: 'DASHBOARD.CHART.LOADING' })}
        </p>
      ) : trendPoints.length === 0 ? (
        <p className="text-sm text-gray-500 py-16 text-center">
          {intl.formatMessage({ id: 'DASHBOARD.CHART.NO_TREND' })}
        </p>
      ) : (
        <ApexChart
          id={`harbor-dashboard-trend-${mode}`}
          key={chartKey}
          options={options}
          series={series}
          type="area"
          width="100%"
          height={280}
        />
      )}
    </div>
  );
};

interface StatusDonutProps {
  items: HarborOperatorDashboardStatusItem[];
  loading?: boolean;
}

export const HarborDashboardStatusDonut = ({
  items,
  loading,
}: StatusDonutProps) => {
  const intl = useIntl();
  const labels = items.map((i) =>
    intl.formatMessage({ id: getBookingStatusMessageId(i.status) })
  );
  const series = items.map((i) => i.count);
  const totalBookings = series.reduce((sum, count) => sum + count, 0);

  const options: ApexOptions = useMemo(
    () => ({
      chart: { type: 'donut', height: 420, toolbar: chartToolbar },
      labels,
      legend: { show: false },
      dataLabels: { enabled: true },
      colors: chartDonutColors,
      stroke: { show: false },
      tooltip: {
        y: {
          formatter: (val) =>
            intl.formatMessage(
              { id: 'DASHBOARD.CHART.BOOKINGS_COUNT' },
              { count: Number(val) }
            ),
        },
      },
      plotOptions: { pie: { donut: { size: '68%' } } },
    }),
    [intl, labels]
  );

  return (
    <div className="card rounded-lg border border-gray-500 shadow-md h-full">
      <div className="card-body p-5">
        <h2 className="text-base font-semibold mb-1 contrast:text-white/90">
          {intl.formatMessage({ id: 'DASHBOARD.CHART.BY_STATUS' })}
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          {intl.formatMessage({ id: 'DASHBOARD.CHART.BY_STATUS_SUBTITLE' })}
        </p>
        {loading ? (
          <p className="text-sm text-gray-500 py-16 text-center">
            {intl.formatMessage({ id: 'DASHBOARD.CHART.LOADING' })}
          </p>
        ) : totalBookings === 0 ? (
          <p className="text-sm text-gray-500 py-16 text-center">
            {intl.formatMessage({ id: 'DASHBOARD.CHART.NO_BOOKINGS' })}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(260px,0.9fr)] lg:items-center">
            <div className="min-w-0">
              <div className="mx-auto w-full max-w-[620px]">
                <ApexChart
                  id="harbor_booking_status_donut"
                  options={options}
                  series={series}
                  type="donut"
                  width="100%"
                  height="420"
                />
              </div>
            </div>
            <div className="space-y-3 lg:pr-2">
              <div className="rounded-xl border border-gray-200 bg-gray-100 px-4 py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {intl.formatMessage({ id: 'DASHBOARD.CHART.TOTAL_BOOKINGS' })}
                </p>
                <p className="mt-1 text-2xl font-semibold text-gray-900">
                  {totalBookings}
                </p>
              </div>
              <div className="space-y-2">
                {items.map((item, index) => {
                  const count = item.count;
                  const percentage =
                    totalBookings > 0 ? (count / totalBookings) * 100 : 0;

                  return (
                    <div
                      key={`${item.label}-${index}`}
                      className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-100 px-4 py-3"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span
                          className="h-3 w-3 rounded-full shrink-0"
                          style={{
                            backgroundColor:
                              chartDonutColors[index % chartDonutColors.length],
                          }}
                        />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">
                            {labels[index]}
                          </p>
                          <p className="text-xs text-gray-500">
                            {intl.formatMessage(
                              { id: 'DASHBOARD.CHART.PERCENT_OF_BOOKINGS' },
                              { percent: percentage.toFixed(1) }
                            )}
                          </p>
                        </div>
                      </div>
                      <p className="shrink-0 text-sm font-semibold text-gray-900">
                        {count}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface TopHarborsChartProps {
  items: HarborOperatorDashboardTopHarbor[];
  /** Months of history the figures cover, shown in the subtitle. */
  months: number;
  loading?: boolean;
}

export const HarborDashboardTopHarborsChart = ({
  items,
  months,
  loading,
}: TopHarborsChartProps) => {
  const intl = useIntl();
  const categories = items.map((h) => h.harbor_name);
  const series = [
    {
      name: intl.formatMessage({ id: 'DASHBOARD.CHART.SERIES_BOOKINGS' }),
      data: items.map((h) => h.bookings),
    },
  ];

  const options: ApexOptions = useMemo(
    () => ({
      chart: { type: 'bar', height: 280, toolbar: chartToolbar },
      plotOptions: {
        bar: { horizontal: true, borderRadius: 4, barHeight: '65%' },
      },
      stroke: {
        show: true,
        width: 0,
        colors: chartStrokeColors,
      },
      colors: chartStrokeColors,
      dataLabels: { enabled: false },
      xaxis: {
        categories,
        labels: { style: { colors: 'var(--tw-gray-500)', fontSize: '11px' } },
      },
      yaxis: {
        labels: {
          style: { colors: 'var(--tw-gray-500)', fontSize: '11px' },
          maxWidth: 140,
        },
      },
      grid: { borderColor: 'var(--tw-gray-200)', strokeDashArray: 4 },
    }),
    [categories]
  );

  return (
    <div className="card rounded-lg border border-gray-500 shadow-md p-4 h-full">
      <div className="mb-3">
        <h3 className="text-base font-semibold text-gray-900">
          {intl.formatMessage({ id: 'DASHBOARD.CHART.TOP_HARBORS' })}
        </h3>
        <p className="text-xs text-gray-500">
          {intl.formatMessage(
            { id: 'DASHBOARD.CHART.TOP_HARBORS_SUBTITLE' },
            { months }
          )}
        </p>
      </div>
      {loading ? (
        <p className="text-sm text-gray-500 py-16 text-center">
          {intl.formatMessage({ id: 'DASHBOARD.CHART.LOADING' })}
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500 py-16 text-center">
          {intl.formatMessage({ id: 'DASHBOARD.CHART.NO_HARBOR_ACTIVITY' })}
        </p>
      ) : (
        <ApexChart options={options} series={series} type="bar" height={280} />
      )}
    </div>
  );
};
